/**
 * Learn — Worked example: coordinating a six-intersection corridor.
 * Framing V10 page 3, built on public/example/NB-Green-Wave.csv.
 * Target query: "traffic signal coordination example".
 */

import Link from '../Link';
import LearnLayout from './LearnLayout';
import TimeDistanceFigure from './TimeDistanceFigure';
import { SCREENSHOT } from '../../lib/screenshot';
import {
  EXAMPLE_CORRIDOR,
  EXAMPLE_CSV_NAME,
  EXAMPLE_CSV_PATH,
  arrivalCheck,
  bandwidth,
  corridorLength,
  cumulativeDistance,
  cycleLength,
  fmt,
  greenWindows,
  offsetSteps,
} from './corridor-example';
import type { PublicRoute } from '../../lib/route';

const CSV_TEXT = `No.,Name,Dist N,V N,V S,OS,A,B,C,D,E,Dir
1,Riverside Dr & Oak St,350,60,55,0,40,22,22,18,18,"NS,N,S,NA,NS"
2,Riverside Dr & Elm Ave,400,60,55,21,40,22,22,18,18,"NS,N,S,NA,NS"
3,Riverside Dr & Pine Rd,300,60,55,45,40,22,22,18,18,"NS,N,S,NA,NS"
4,Riverside Dr & Cedar Ln,450,60,55,63,40,22,22,18,18,"NS,N,S,NA,NS"
5,Riverside Dr & Birch Way,350,60,55,90,40,22,22,18,18,"NS,N,S,NA,NS"
6,Riverside Dr & Maple Ct,0,60,55,111,40,22,22,18,18,"NS,N,S,NA,NS"`;

export default function WorkedExamplePage({ route }: { route: PublicRoute }) {
  const cycle = cycleLength(EXAMPLE_CORRIDOR[0]);
  const dist = cumulativeDistance(EXAMPLE_CORRIDOR);
  const steps = offsetSteps(EXAMPLE_CORRIDOR);
  const nbRows = arrivalCheck(EXAMPLE_CORRIDOR, 'N');
  const nbBand = bandwidth(EXAMPLE_CORRIDOR, 'N');
  const sbBand = bandwidth(EXAMPLE_CORRIDOR, 'S');
  const nbWindows = greenWindows(EXAMPLE_CORRIDOR[0], 'N');
  const sbWindows = greenWindows(EXAMPLE_CORRIDOR[0], 'S');
  const shot = SCREENSHOT.riversidePmPeak;

  return (
    <LearnLayout
      route={route}
      heading="Worked example: coordinating a six-intersection traffic signal corridor"
      lede={`Every number for the Riverside Drive corridor that ships with SigTDD as ${EXAMPLE_CSV_NAME}: the input file, the geometry, the phase plan, the offsets and the resulting time-distance (time-space) diagram with its northbound green wave. Follow it in the tool, then swap in your own corridor.`}
    >
      <h2 id="input">1. The input file</h2>
      <p>
        The corridor is described in one CSV, one row per signalised intersection from south to north.
        This is the exact file, which you can{' '}
        <a href={EXAMPLE_CSV_PATH} download>download</a> and import, or load from the Examples list in the
        tool's Load menu:
      </p>
      <pre><code>{CSV_TEXT}</code></pre>
      <table className="learn-table">
        <thead>
          <tr>
            <th>Column</th>
            <th>Meaning</th>
            <th>Unit</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>No.</td><td>Sequence from the southern end</td><td>—</td></tr>
          <tr><td>Name</td><td>Corridor and cross street</td><td>—</td></tr>
          <tr><td>Dist N</td><td>Distance to the next intersection north; 0 at the last</td><td>m</td></tr>
          <tr><td>V N, V S</td><td>Northbound and southbound design speeds</td><td>km/h</td></tr>
          <tr><td>OS</td><td>Offset: start of this cycle after the corridor reference</td><td>s</td></tr>
          <tr><td>A–E</td><td>Phase durations in sequence, amber included</td><td>s</td></tr>
          <tr><td>Dir</td><td>Which through movement each phase serves: NS both, N northbound, S southbound, NA neither</td><td>—</td></tr>
        </tbody>
      </table>

      <h2 id="geometry">2. Geometry</h2>
      <p>
        The five links add up to a corridor of {corridorLength(EXAMPLE_CORRIDOR)} m. Cumulative distance,
        which is the vertical position of each signal on the diagram, is:
      </p>
      <ul>
        {EXAMPLE_CORRIDOR.map((int, i) => (
          <li key={int.no}>
            #{int.no} {int.name} — {dist[i]} m
          </li>
        ))}
      </ul>
      <p>
        Northbound design speed is {EXAMPLE_CORRIDOR[0].speedNorth} km/h on every link; southbound is{' '}
        {EXAMPLE_CORRIDOR[0].speedSouth} km/h. Different speeds per direction are common where grade or
        lane arrangement differs; the tool keeps both.
      </p>

      <h2 id="phase">3. The phase plan</h2>
      <p>
        All six intersections run the same five-phase sequence, which keeps the example readable. Phase A
        ({EXAMPLE_CORRIDOR[0].phases[0].duration} s) serves both through movements; B ({EXAMPLE_CORRIDOR[0].phases[1].duration} s) is
        northbound only, as with a leading northbound right turn that keeps the through running; C
        ({EXAMPLE_CORRIDOR[0].phases[2].duration} s) is the southbound equivalent; D ({EXAMPLE_CORRIDOR[0].phases[3].duration} s) is the
        cross street and stops the corridor; E ({EXAMPLE_CORRIDOR[0].phases[4].duration} s) returns to both
        directions. The cycle length is the sum: <strong>{cycle} s</strong>. Deducting the 4 s amber at the end
        of each green run, the usable green windows measured from each intersection's own cycle start are:
      </p>
      <ul>
        <li>Northbound: {nbWindows.map((w) => `${fmt(w.start)}–${fmt(w.end)} s`).join(' and ')} (A and B run together, then E).</li>
        <li>Southbound: {sbWindows.map((w) => `${fmt(w.start)}–${fmt(w.end)} s`).join(', ')} (A alone, then C, then E).</li>
      </ul>

      <h2 id="offset">4. Offsets from travel time</h2>
      <p>
        The plan targets northbound progression, so each offset is the cumulative northbound travel time
        from Oak Street (offset 0). The arithmetic is set out in full on the{' '}
        <Link to="/learn/green-wave-offset" event="learn-open" source="learn">offset page</Link>; the results
        are:
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>#</th>
            <th className="num">Link (m)</th>
            <th className="num">Travel (s)</th>
            <th className="num">Cumulative (s)</th>
            <th className="num">Offset (s)</th>
          </tr>
        </thead>
        <tbody>
          {steps.map((s) => (
            <tr key={s.no}>
              <td>{s.no}</td>
              <td className="num">{s.spacing || '—'}</td>
              <td className="num">{s.no === 1 ? '—' : fmt(s.travel, 1)}</td>
              <td className="num">{fmt(s.cumulative, 1)}</td>
              <td className="num">{s.csvOffset}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        In the tool the same result comes from <strong>Sequence → NB</strong> in the toolbar; the table is what
        that button computes.
      </p>

      <h2 id="check">5. Checking the wave</h2>
      <p>
        Send one vehicle north from Oak Street at the start of green (corridor time 0 s) at 60 km/h and
        record where it is, in each downstream signal's own cycle, when it arrives:
      </p>
      <table className="learn-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Intersection</th>
            <th className="num">Arrives at (s)</th>
            <th className="num">In that signal's cycle (s)</th>
            <th>Green?</th>
          </tr>
        </thead>
        <tbody>
          {nbRows.map((r) => (
            <tr key={r.no}>
              <td>{r.no}</td>
              <td>{r.name}</td>
              <td className="num">{fmt(r.arrival, 1)}</td>
              <td className="num">{fmt(r.inCycle, 1)}</td>
              <td>{r.green ? 'Yes' : 'No'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Every arrival lands within a second of the start of phase A, so the leading edge of the platoon
        never waits. The trailing edge, released {fmt(nbWindows[0].end)} s later at the end of phase B, follows
        the same trajectory {fmt(nbWindows[0].end)} s behind and also meets green throughout, which is why the
        northbound bandwidth is the full {fmt(nbBand)} s ({fmt((nbBand / cycle) * 100)} percent of the cycle).
        Southbound, with these offsets, the bandwidth is {fmt(sbBand)} s: the plan favours the northbound peak
        by design, and a PM plan on the same corridor would reverse it.
      </p>

      <figure className="learn-figure">
        <TimeDistanceFigure
          corridor={EXAMPLE_CORRIDOR}
          title="Riverside Drive example corridor: time-distance diagram with the northbound progression band"
          description={`The finished northbound plan: phase bars stepped by ${steps.slice(1).map((s) => fmt(s.travel)).join(', ')} seconds and a ${fmt(nbBand)}-second band through all six signals.`}
        />
        <figcaption>The finished plan drawn from the CSV above.</figcaption>
      </figure>

      <h2 id="tool">6. The same corridor in the tool</h2>
      <figure className="learn-figure">
        <img src={shot.src} width={shot.width} height={shot.height} alt={shot.alt} loading="lazy" decoding="async" />
        <figcaption>
          {shot.caption}. The table on the right is the CSV; the offset slider under the canvas moves the
          selected intersection's bar; the header shows the bandwidth for each direction.
        </figcaption>
      </figure>
      <p>Working through it live:</p>
      <ol>
        <li><strong>Load</strong> → Examples → NB-Green-Wave, or <strong>Import</strong> the CSV.</li>
        <li>Turn on <em>NB Sequence</em> in Display Options and turn <em>SB Sequence</em> off to isolate the northbound band.</li>
        <li>Select Elm Avenue and drag its offset away from 21 s: the band narrows immediately, then breaks. Drag it back.</li>
        <li>Use <strong>Sequence → SB</strong> to re-time the corridor for the southbound peak and watch the band swap sides; duplicate the plan first from the plan selector to keep both.</li>
        <li>Change the cycle by adding 10 s to phase A at every intersection and re-run the sequence; the offsets change because the arithmetic is unchanged but the wrap point moved.</li>
        <li>Save as your own corridor (a free account) to share a read-only link with a reviewer.</li>
      </ol>

      <h2 id="own">7. Doing it for your own corridor</h2>
      <p>
        Replace the CSV rows with your signals from south to north: measured stop-line spacing, the platoon
        speed you observe rather than the posted limit, the actual phase splits from the controller and the
        direction each phase serves. If the corridor exists in Synchro, the{' '}
        <Link to="/learn/synchro-utdf-import" event="learn-open" source="learn">UTDF import</Link> brings the
        geometry and timings across. Then run Sequence for the peak direction, read the bandwidth for the
        other direction, and decide what the second plan should favour. The{' '}
        <Link to="/docs" event="docs-open" source="learn">documentation</Link> covers every control used above.
      </p>
    </LearnLayout>
  );
}
